import { Box, HStack, Flex, Stack, Button, Icon } from '@chakra-ui/react';
import { get, isEqual } from 'lodash';
import { Avatar, UploadAvatar, CameraIcon, CloseIcon } from '@halo/ui-chat-react';
import { Dialog, toaster } from '@halo/ui-react';
import { useAppStore } from '@halo/chat-sdk-react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { useForm } from 'react-hook-form';
import { useActionsStore } from '@/store/actions';
import { useReducerStore } from '@/scripts/hooks';
import { userApis } from '@/configs/apis';
import { ButtonTrigger } from './components/ButtonTrigger';
import { User } from './components/User';
import { Form } from './components/Form';

export interface UserState {
  avatarFile: File | null;
  avatarSrc: string;
  first_name: string;
  last_name: string;
  username: string;
  bio: string;
  birth_day: number;
  gender: string | undefined;
  loading: boolean;
  popper: boolean;
}

export const UserOnBoarding = memo(function UserOnBoarding() {
  const { t } = useTranslation();
  const { userOnboardingIsOpen, setUserOnboardingIsOpen } = useActionsStore();
  const { user, client } = useAppStore(useShallowEnhance(['user', 'client']));
  const form = useForm<UserState>();
  const { handleSubmit } = form;

  const { state, dispatch } = useReducerStore<UserState>({
    avatarFile: null,
    avatarSrc: '',
    first_name: '',
    last_name: '',
    username: '',
    bio: '',
    birth_day: 0,
    gender: undefined,
    loading: false,
    popper: true,
  });
  const handleUpdateProfileUser = useCallback(async () => {
    client?.Api.user.queryUserInfoGlobal(user?.user_id);
  }, [user?.user_id, client]);

  const handleAvatarChange = (file: File) => {
    dispatch([
      {
        type: 'avatarFile',
        payload: file,
      },
      {
        type: 'avatarSrc',
        payload: URL.createObjectURL(file) || '',
      },
    ]);
    return Promise.resolve();
  };
  const handleClose = () => {
    setUserOnboardingIsOpen(false);
    userApis.removeIsNewUser();
  };
  const { run: submit } = useDebounceFn(
    handleSubmit(
      async () => {
        dispatch({
          type: 'loading',
          payload: true,
        });
        try {
          let avatar_url: string | undefined = '';
          if (state.avatarFile) {
            avatar_url = await client?.Api.common.getUploadAvatar({ file: state.avatarFile });
          }
          await client?.Api.user.uploadUserProfile([
            ...(state.username && state.username !== user.username
              ? [
                  {
                    cate: 'username',
                    data: {
                      username: state.username,
                    },
                  },
                ]
              : []),
            {
              cate: 'extends',
              data: {
                birthday: state.birth_day && state.birth_day > 0 ? String(state.birth_day) : '',
                ...(!isEqual(state.gender, undefined) && { gender: state.gender }),
                bio: state.bio,
              },
            },
            {
              cate: 'base',
              data: {
                avatar: avatar_url || '',
                first_name: state.first_name || get(user, 'first_name'),
                last_name: state.last_name || '',
              },
            },
          ]);

          handleUpdateProfileUser();
          handleClose();
        } catch (error) {
          toaster.error({
            title: error,
          });
        } finally {
          dispatch({
            type: 'loading',
            payload: false,
          });
        }
      },
      (error) => {
        const message = error?.first_name?.message;
        if (message) {
          toaster.error({
            title: message,
          });
        }
      }
    ),
    { wait: 400 }
  );
  useEffect(() => {
    dispatch([
      {
        type: 'first_name',
        payload: get(user, 'first_name'),
      },
      {
        type: 'last_name',
        payload: get(user, 'last_name'),
      },
      {
        type: 'username',
        payload: get(user, 'username'),
      },
      {
        type: 'avatarSrc',
        payload: get(user, 'avatar'),
      },
    ]);
  }, [user, dispatch]);

  const containerRef = useRef<HTMLDivElement>(null);
  const { run: handleScroll } = useDebounceFn(
    () => {
      const popper = containerRef.current?.querySelector('.react-datepicker-popper');
      if (!popper) return;
      const popperRect = popper.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (containerRect) {
        const isInContainer =
          popperRect.top >= containerRect.top - 100 &&
          popperRect.bottom <= containerRect.bottom + 100;

        if (!isInContainer) {
          dispatch({
            type: 'popper',
            payload: false,
          });
        } else {
          dispatch({
            type: 'popper',
            payload: true,
          });
        }
      }
    },
    { wait: 300 }
  );
  return (
    <Dialog.Root open={userOnboardingIsOpen} placement="center">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content w="480px" maxW="100%" bg="bg.subtle" maxH="calc(100vh - 48px)">
          <Dialog.Header
            className="Header"
            pt="5"
            px="5"
            pb="2.5"
            justifyContent="space-between"
            alignItems="center"
          >
            <Dialog.Title fontSize="md" fontWeight="semibold">
              {t('userOnboarding.title')}
            </Dialog.Title>
            <Icon color="fg.muted" cursor="pointer" onClick={handleClose}>
              <CloseIcon />
            </Icon>
          </Dialog.Header>
          <Dialog.Body
            overflow="auto"
            className="scrollHover"
            p={0}
            ref={containerRef}
            onScroll={handleScroll}
          >
            <Stack>
              <HStack w="full" px="4" gap="4" roundedTopLeft="2xl" roundedTopRight="2xl">
                <Box w={16} height={16}>
                  <UploadAvatar onChange={handleAvatarChange}>
                    <Box
                      pos="relative"
                      cursor="pointer"
                      css={{
                        '&:hover': {
                          '--show-update-avatar': 'flex',
                        },
                      }}
                    >
                      <Avatar
                        css={{
                          outlineWidth: '3px',
                          outlineColor: 'bg',
                          outlineStyle: 'solid',
                        }}
                        src={state.avatarSrc}
                        name={get(user, 'name')}
                        w={16}
                        height={16}
                      />
                      <Flex
                        pos="absolute"
                        top={0}
                        left={0}
                        alignItems="center"
                        justifyContent="center"
                        zIndex={10}
                        w={16}
                        height={16}
                        bg="blackAlpha.70"
                        rounded="50%"
                        display="var(--show-update-avatar, none)"
                      >
                        <CameraIcon width="24px" height="24px" />
                      </Flex>
                    </Box>
                  </UploadAvatar>
                </Box>
                <ButtonTrigger>
                  <User avatar={state.avatarSrc} {...state} />
                </ButtonTrigger>
              </HStack>
              <Form user={user} form={form} dispatch={dispatch} popper={state.popper} />
            </Stack>
          </Dialog.Body>
          <Dialog.Footer>
            <HStack gap="4">
              <Button bg="white" color="fg" px="6" borderRadius="xl" onClick={handleClose}>
                {t('buttons.skip')}
              </Button>
              <Button px="6" borderRadius="xl" onClick={submit} loading={state.loading}>
                {t('buttons.save')}
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
});
